import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Directive, ElementRef, ViewContainerRef, ContentChild, HostListener, Optional, SkipSelf, ContentChildren, Input, HostBinding, Component, NgModule } from '@angular/core';
import * as i2$1 from '@angular/router';
import { NavigationEnd, RouterLink, RouterModule } from '@angular/router';
import { SiLocalizationModule } from '@simpl/siemens-brand-localize';
import { first, filter, observeOn } from 'rxjs/operators';
import * as i1 from '@angular/animations';
import { query, style, group, animate, keyframes } from '@angular/animations';
import * as i2 from '@angular/cdk/platform';
import { Subject, asyncScheduler } from 'rxjs';
import { siFluidEasingFunction, siFluidEasing, siSnappyEasing } from '@simpl/siemens-brand-ng/tools';
import { SiDropdownMenuDirective } from '@simpl/siemens-brand-ng/dropdown-menu';

class SiHeaderCollapseDirective {
    constructor(renderer2, elementRef) {
        this.renderer2 = renderer2;
        this.elementRef = elementRef;
        this._status = 'collapsed';
    }
    set status(value) {
        if (value !== this._status) {
            this._status = value;
            if (value === 'expanded') {
                this.renderer2.addClass(this.elementRef.nativeElement, 'show');
            }
            else {
                this.renderer2.removeClass(this.elementRef.nativeElement, 'show');
            }
        }
    }
}
SiHeaderCollapseDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderCollapseDirective, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
SiHeaderCollapseDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiHeaderCollapseDirective, selector: "[siHeaderCollapse]", exportAs: ["siHeaderCollapse"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderCollapseDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siHeaderCollapse]',
                    exportAs: 'siHeaderCollapse',
                }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ElementRef }]; } });

class SiHeaderContainerDirective {
}
SiHeaderContainerDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderContainerDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiHeaderContainerDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiHeaderContainerDirective, selector: "[siHeaderContainer]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderContainerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siHeaderContainer]',
                }]
        }] });

class SiHeaderDropdownMenuDirective {
    constructor(templateRef) {
        this.templateRef = templateRef;
        this.open = false;
    }
}
SiHeaderDropdownMenuDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderDropdownMenuDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive });
SiHeaderDropdownMenuDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiHeaderDropdownMenuDirective, selector: "[siHeaderDropdownMenu]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderDropdownMenuDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siHeaderDropdownMenu]',
                }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }]; } });

class SiHeaderTogglerDirective {
}
SiHeaderTogglerDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderTogglerDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiHeaderTogglerDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiHeaderTogglerDirective, selector: "[siHeaderToggler]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderTogglerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siHeaderToggler]',
                }]
        }] });

const easedStates = Array(100).fill(0)
    .map((v, i) => siFluidEasingFunction((i + 1) / 100));
const expandAnimation = [
    query('.si-header-collapse', style({
        width: `{{targetCircleRadius}}px`,
        height: `{{targetCircleRadius}}px`,
        transform: 'scale(0)',
        transformOrigin: 'top right',
        borderBottomLeftRadius: `{{targetCircleRadius}}px`,
        overflow: 'hidden',
    })),
    query('.si-header-collapse-wrapper', style({
        transformOrigin: 'top right',
        paddingLeft: `{{paddingRight}}px`,
        paddingBottom: `{{paddingBottom}}px`,
    })),
    query('.si-header-nav-main .si-header-item', [
        style({ visibility: 'hidden' }),
    ], { optional: true }),
    group([
        query('.si-header-collapse', animate('400ms', keyframes(easedStates.map(state => style({ transform: `scale(${state})` }))))),
        query('.si-header-collapse-wrapper', animate('400ms', keyframes(easedStates.map(state => style({ transform: `scale(${1 / state})` }))))),
        query('.si-header-nav-main .si-header-item', [
            style({ visibility: 'visible', transform: 'translateX(-100%)' }),
            animate(`400ms ${siFluidEasing}`, style({ transform: 'translateX(0)' })),
        ], { optional: true }),
    ]),
];
const openDropdownAnimation = [
    group([
        query('.si-header-dropdown-nav .nav-link', [
            style({ transform: 'translateX(100%)' }),
            animate(`400ms ${siSnappyEasing}`, style({ transform: 'translateX(0)' })),
        ], { optional: true }),
        query('.si-header-link-close', [
            style({ transform: 'translateX(-100%)' }),
            animate(`400ms ${siSnappyEasing}`, style({ transform: 'translateX(0)' })),
        ], { optional: true }),
    ]),
];
const closeDropdownAnimation = [
    query('.si-header-nav-main .nav-item, .si-header-dropdown-nav .nav-link', [
        style({ transform: 'translateX(-100%)' }),
        animate(`400ms ${siSnappyEasing}`, style({ transform: 'translateX(0)' })),
    ], { optional: true }),
];
const navbarExpandedOpenFirstDropdownAnimation = [
    query('.si-header-dropdown-menu', style({
        width: `{{targetCircleRadius}}px`,
        height: `{{targetCircleRadius}}px`,
        transform: 'scale(0)',
        transformOrigin: 'top right',
        borderBottomLeftRadius: `{{targetCircleRadius}}px`,
        overflow: 'hidden',
    })),
    query('.si-header-dropdown-menu-wrapper', style({
        transformOrigin: 'top right',
        paddingLeft: `{{paddingRight}}px`,
        paddingBottom: `{{paddingBottom}}px`,
    })),
    group([
        query('.si-header-dropdown-menu', animate('400ms', keyframes(easedStates.map(state => style({ transform: `scale(${state})` }))))),
        query('.si-header-dropdown-menu-wrapper', animate('400ms', keyframes(easedStates.map(state => style({ transform: `scale(${1 / state})` }))))),
        query('.si-header-dropdown-nav', [
            style({ transform: 'translateX(100%)', opacity: '0' }),
            animate(`200ms ${siSnappyEasing}`, style({ transform: 'translateX(0)', opacity: '1' })),
        ]),
    ]),
];
const navbarExpandedOpenNextDropdownAnimation = [
    group([
        query('.si-header-dropdown-menu:nth-last-child(1)', [
            style({ background: 'transparent' }),
            query('.si-header-dropdown-nav', [
                style({ transform: 'translateX(100%)', opacity: '0' }),
                animate(`200ms ${siSnappyEasing}`, style({ transform: 'translateX(0)', opacity: '1' })),
            ]),
        ]),
        query('.si-header-dropdown-menu:nth-last-child(2)', [
            query('.si-header-dropdown-head', style({ visibility: 'hidden' })),
            query('.si-header-dropdown-nav', [
                style({ transform: 'translateX(0)', opacity: '1' }),
                animate(`200ms ${siSnappyEasing}`, style({ transform: 'translateX(-100%)', opacity: '0' })),
            ]),
        ]),
    ]),
];
const navbarExpandedCloseNextDropdownAnimation = [
    group([
        query('.si-header-dropdown-menu:nth-last-child(1)', [
            style({ background: 'transparent' }),
            query('.container > :not(.si-header-dropdown-nav), .container-fluid > :not(.si-header-dropdown-nav)', style({ visibility: 'hidden' })),
            query('.si-header-dropdown-nav', [
                style({ transform: 'translateX(0)', opacity: '1' }),
                animate(`200ms ${siSnappyEasing}`, style({ transform: 'translateX(100%)', opacity: '0' })),
            ]),
        ]),
        query('.si-header-dropdown-menu:nth-last-child(2)', [
            query('.si-header-dropdown-nav', [
                style({ transform: 'translateX(-100%)', opacity: '0' }),
                animate(`200ms ${siSnappyEasing}`, style({ transform: 'translateX(0)', opacity: '1' })),
            ]),
        ]),
    ]),
];

class SiHeaderDirective {
    constructor(renderer2, animationBuilder, elementRef, platform) {
        this.renderer2 = renderer2;
        this.animationBuilder = animationBuilder;
        this.elementRef = elementRef;
        this.platform = platform;
        this._navbarCollapsed = true;
        this.openDropdowns = new Map();
        this._expandOrDropdownOpenComplete = new Subject();
        this._closeDropdownComplete = new Subject();
    }
    get navbarCollapsed() {
        return this._navbarCollapsed;
    }
    get navbarExpandedOrDropdownOpen() {
        return !this._navbarCollapsed || !!this.openDropdowns.size;
    }
    ngOnInit() {
        this.expandAnimation = this.animationBuilder.build(expandAnimation);
        this.openDropdownAnimation = this.animationBuilder.build(openDropdownAnimation);
        this.closeDropdownAnimation = this.animationBuilder.build(closeDropdownAnimation);
        this.navbarExpandedOpenFirstDropdownAnimation = this.animationBuilder.build(navbarExpandedOpenFirstDropdownAnimation);
        this.navbarExpandedOpenNextDropdownAnimation = this.animationBuilder.build(navbarExpandedOpenNextDropdownAnimation);
        this.navbarExpandedCloseNextDropdownAnimation = this.animationBuilder.build(navbarExpandedCloseNextDropdownAnimation);
        this._updateCollapseAnimationAttributes();
    }
    ngAfterViewInit() {
        if (this.togglerElement) {
            this.renderer2.listen(this.togglerElement.nativeElement, 'click', () => {
                if (this._navbarCollapsed) {
                    this.expandNavbar();
                }
                else {
                    this.closeAll();
                }
            });
        }
    }
    closeAll() {
        while (this.navbarContainerViewRef.length) {
            this.navbarContainerViewRef.remove();
        }
        this.collapseElement.status = 'collapsed';
        this._navbarCollapsed = true;
        this.openDropdowns.forEach(value => value.menu.open = false);
        this.openDropdowns.clear();
    }
    expandNavbar() {
        this.collapseElement.status = 'expanded';
        this.currentAnimation = this.expandAnimation.create(this.elementRef.nativeElement, {
            params: {
                targetCircleRadius: this.targetCircleRadius,
                paddingRight: this.paddingRight,
                paddingBottom: this.paddingBottom,
                target: 'navbar-collapse',
            },
        });
        this.currentAnimation.play();
        this.currentAnimation.onDone(() => {
            this.currentAnimation.destroy();
            this.currentAnimation = undefined;
            this._expandOrDropdownOpenComplete.next();
        });
        this._navbarCollapsed = false;
    }
    openDropdown(dropdown, trigger, context) {
        const viewRef = this.navbarContainerViewRef.createEmbeddedView(dropdown.templateRef, context);
        dropdown.open = true;
        this.openDropdowns.set(viewRef, { menu: dropdown, trigger });
        let animation;
        if (this.navbarExpand) {
            if (this.navbarContainerViewRef.length > 1) {
                animation = this.navbarExpandedOpenNextDropdownAnimation.create(this.elementRef.nativeElement);
                animation.onDone(() => {
                    const previousViewRef = this.navbarContainerViewRef.get(this.navbarContainerViewRef.length - 2);
                    for (const rootNode of previousViewRef.rootNodes) {
                        this.renderer2.setAttribute(rootNode, 'hidden', 'true');
                    }
                });
            }
            else {
                animation = this.navbarExpandedOpenFirstDropdownAnimation.create(this.elementRef.nativeElement, {
                    params: {
                        targetCircleRadius: this.targetCircleRadius,
                        paddingRight: this.paddingRight,
                        paddingBottom: this.paddingBottom,
                        target: 'navbar-collapse',
                    },
                });
            }
        }
        else {
            animation = this.openDropdownAnimation.create(this.elementRef.nativeElement);
        }
        animation.play();
        animation.onDone(() => {
            try {
                this._expandOrDropdownOpenComplete.next();
                animation.destroy();
            }
            catch (e) { /* Nothing to do: https://github.com/angular/angular/issues/26095 */ }
        });
        return viewRef;
    }
    closeDropdown() {
        let animation;
        if (this.navbarExpand) {
            if (this.navbarContainerViewRef.length > 1) {
                const previousViewRef = this.navbarContainerViewRef.get(this.navbarContainerViewRef.length - 2);
                for (const rootNode of previousViewRef.rootNodes) {
                    this.renderer2.removeAttribute(rootNode, 'hidden');
                }
                animation = this.navbarExpandedCloseNextDropdownAnimation.create(this.elementRef.nativeElement);
                animation.onDone(() => this.destroyDropdown());
            }
            else {
                this.destroyDropdown();
                this._closeDropdownComplete.next();
            }
        }
        else {
            this.destroyDropdown();
            animation = this.closeDropdownAnimation.create(this.elementRef.nativeElement);
        }
        if (animation) {
            animation.play();
            animation.onDone(() => {
                try {
                    this._closeDropdownComplete.next();
                    animation.destroy();
                }
                catch (e) { /* Nothing to do: https://github.com/angular/angular/issues/26095 */ }
            });
        }
    }
    _updateCollapseAnimationAttributes() {
        if (this.platform.isBrowser) {
            const menuHeight = window.innerHeight - this.elementRef.nativeElement.clientHeight;
            const menuWidth = this.elementRef.nativeElement.clientWidth; // This is aware of potential scrollbars
            this.navbarExpand = !!+getComputedStyle(this.elementRef.nativeElement).getPropertyValue('--navbar-expand');
            this.targetCircleRadius = Math.sqrt(menuHeight * menuHeight + menuWidth * menuWidth);
            this.paddingRight = this.targetCircleRadius - menuWidth;
            this.paddingBottom = this.targetCircleRadius - menuHeight;
        }
    }
    destroyDropdown() {
        const viewRef = this.navbarContainerViewRef.get(this.navbarContainerViewRef.length - 1);
        const toClose = this.openDropdowns.get(viewRef);
        toClose.menu.open = false;
        this._closeDropdownComplete.pipe(first()).subscribe(() => toClose.trigger?._focus());
        toClose.trigger?._focus();
        this.openDropdowns.delete(viewRef);
        this.navbarContainerViewRef.remove();
    }
}
SiHeaderDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderDirective, deps: [{ token: i0.Renderer2 }, { token: i1.AnimationBuilder }, { token: i0.ElementRef }, { token: i2.Platform }], target: i0.ɵɵFactoryTarget.Directive });
SiHeaderDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiHeaderDirective, selector: "[siHeader]", host: { listeners: { "keydown.esc": "closeDropdown()", "window:resize": "_updateCollapseAnimationAttributes()" } }, queries: [{ propertyName: "togglerElement", first: true, predicate: SiHeaderTogglerDirective, descendants: true, read: ElementRef }, { propertyName: "collapseElement", first: true, predicate: SiHeaderCollapseDirective, descendants: true }, { propertyName: "navbarContainerViewRef", first: true, predicate: SiHeaderContainerDirective, descendants: true, read: ViewContainerRef }], exportAs: ["siHeader"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siHeader]',
                    exportAs: 'siHeader',
                }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i1.AnimationBuilder }, { type: i0.ElementRef }, { type: i2.Platform }]; }, propDecorators: { togglerElement: [{
                type: ContentChild,
                args: [SiHeaderTogglerDirective, { read: ElementRef }]
            }], collapseElement: [{
                type: ContentChild,
                args: [SiHeaderCollapseDirective]
            }], navbarContainerViewRef: [{
                type: ContentChild,
                args: [SiHeaderContainerDirective, { read: ViewContainerRef }]
            }], closeDropdown: [{
                type: HostListener,
                args: ['keydown.esc']
            }], _updateCollapseAnimationAttributes: [{
                type: HostListener,
                args: ['window:resize']
            }] } });

class SiHeaderFocusEntryDirective {
    constructor(siHeaderDirective, elementRef) {
        this.siHeaderDirective = siHeaderDirective;
        this.elementRef = elementRef;
    }
    ngOnInit() {
        this.siHeaderDirective._expandOrDropdownOpenComplete.pipe(first())
            .subscribe(() => this.elementRef.nativeElement.focus());
    }
}
SiHeaderFocusEntryDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderFocusEntryDirective, deps: [{ token: SiHeaderDirective }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
SiHeaderFocusEntryDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiHeaderFocusEntryDirective, selector: "[siSiHeaderFocusEntry]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderFocusEntryDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siSiHeaderFocusEntry]',
                }]
        }], ctorParameters: function () { return [{ type: SiHeaderDirective }, { type: i0.ElementRef }]; } });

class SiHeaderMenuDirective {
    constructor(renderer, platform, elRef) {
        this.renderer = renderer;
        this.platform = platform;
        this.elRef = elRef;
    }
    ngOnInit() {
        this.underline = this.renderer.createElement('div');
        this.renderer.addClass(this.underline, 'si-header-underline');
        this.renderer.setAttribute(this.underline, 'aria-hidden', 'true');
        this.renderer.appendChild(this.elRef.nativeElement, this.underline);
        if (this.platform.isBrowser) {
            this.resizeObserver = new ResizeObserver(() => this.moveUnderline(this.currentElement));
        }
    }
    onLeave() {
        if (this.routedElement && !this.dropdownElement) {
            this.updateUnderline(this.routedElement);
        }
        else if (this.dropdownElement && this.dropdownElement !== this.currentElement) {
            this.updateUnderline(this.dropdownElement);
        }
        else if (!this.dropdownElement && !this.routedElement) {
            this.renderer.setStyle(this.underline, 'width', `0`);
        }
    }
    updateUnderlineByHover(hoveredElement) {
        this.updateUnderline(hoveredElement);
    }
    updateUnderlineByRoute(routedElement) {
        this.routedElement = routedElement ?? this.routedElement;
        if (this.routedElement) {
            this.updateUnderline(this.routedElement);
        }
        this.dropdownElement = undefined;
    }
    updateUnderlineByDropdown(openElement) {
        this.updateUnderline(openElement);
        this.dropdownElement = openElement;
    }
    updateUnderline(element) {
        if (this.platform.isBrowser) {
            if (this.resizeObserver) {
                if (this.currentElement) {
                    this.resizeObserver.unobserve(this.currentElement);
                }
                this.resizeObserver.observe(element);
            }
            this.currentElement = element;
            this.moveUnderline(element);
        }
        else {
            if (this.currentElement) {
                this.renderer.removeClass(this.currentElement, 'si-header-server-underline');
            }
            this.renderer.addClass(element, 'si-header-server-underline');
            this.currentElement = element;
        }
    }
    moveUnderline(element) {
        this.renderer.setStyle(this.underline, 'left', `${element.offsetLeft}px`);
        this.renderer.setStyle(this.underline, 'width', `${element.offsetWidth}px`);
    }
}
SiHeaderMenuDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderMenuDirective, deps: [{ token: i0.Renderer2 }, { token: i2.Platform }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
SiHeaderMenuDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiHeaderMenuDirective, selector: "[siHeaderMenu]", host: { listeners: { "mouseleave": "onLeave()" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderMenuDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siHeaderMenu]',
                }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i2.Platform }, { type: i0.ElementRef }]; }, propDecorators: { onLeave: [{
                type: HostListener,
                args: ['mouseleave']
            }] } });

class SiHeaderItemDirective {
    constructor(navbarDirective, elementRef, router, headerMenuDirective, navDropdownMenuDirective, navItemDirective) {
        this.navbarDirective = navbarDirective;
        this.elementRef = elementRef;
        this.router = router;
        this.headerMenuDirective = headerMenuDirective;
        this.navDropdownMenuDirective = navDropdownMenuDirective;
        this.navItemDirective = navItemDirective;
        if (this.headerMenuDirective && !this.navItemDirective) {
            this.router.events.pipe(filter(event => event instanceof NavigationEnd), 
            // updateUnderlineFromRoute must run in the next tick because the active class is applied after NavigationEnd is fired
            observeOn(asyncScheduler), filter(() => this.hasActiveLinks()))
                .subscribe(() => this.headerMenuDirective.updateUnderlineByRoute(this.elementRef.nativeElement));
        }
    }
    _globalClick(event) {
        if (this._dropdownMenu?.isOpen && !event.composedPath().includes(this.elementRef.nativeElement)) {
            this._dropdownMenu.close();
        }
    }
    get hasDropdown() {
        return !!this._headerDropdownMenu || !!this._dropdownMenu;
    }
    openDropdown(trigger) {
        if (this._headerDropdownMenu) {
            this.navbarDirective.openDropdown(this._headerDropdownMenu, trigger);
            if (this.headerMenuDirective && !this.navItemDirective) {
                this.headerMenuDirective.updateUnderlineByDropdown(this.elementRef.nativeElement);
                this.navbarDirective._closeDropdownComplete
                    .pipe(first(() => !this._headerDropdownMenu.open)) // This is also fired when inner dropdowns are closing -> ignore them
                    .subscribe(() => this.headerMenuDirective.updateUnderlineByRoute());
            }
        }
        else if (this._dropdownMenu) {
            this._dropdownMenu.toggle();
        }
    }
    onEnter() {
        if (this.headerMenuDirective && !this.navDropdownMenuDirective?.open && !this.navItemDirective) {
            this.headerMenuDirective.updateUnderlineByHover(this.elementRef.nativeElement);
        }
    }
    isLinkActive(link) {
        return link.urlTree
            ? this.router.isActive(link.urlTree, {
                paths: 'subset',
                queryParams: 'subset',
                fragment: 'ignored',
                matrixParams: 'ignored',
            })
            : false;
    }
    hasActiveLinks() {
        return this.links.some(link => this.isLinkActive(link)) || this.linksWithHrefs.some(link => this.isLinkActive(link));
    }
}
SiHeaderItemDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderItemDirective, deps: [{ token: SiHeaderDirective }, { token: i0.ElementRef }, { token: i2$1.Router }, { token: SiHeaderMenuDirective, optional: true }, { token: SiHeaderDropdownMenuDirective, optional: true }, { token: SiHeaderItemDirective, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.Directive });
SiHeaderItemDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiHeaderItemDirective, selector: "[siHeaderItem]", host: { listeners: { "document:click": "_globalClick($event)", "mouseenter": "onEnter()" } }, queries: [{ propertyName: "_headerDropdownMenu", first: true, predicate: SiHeaderDropdownMenuDirective, descendants: true }, { propertyName: "_dropdownMenu", first: true, predicate: SiDropdownMenuDirective, descendants: true }, { propertyName: "links", predicate: RouterLink }, { propertyName: "linksWithHrefs", predicate: RouterLink }], exportAs: ["siHeaderItem"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siHeaderItem]',
                    exportAs: 'siHeaderItem',
                }]
        }], ctorParameters: function () { return [{ type: SiHeaderDirective }, { type: i0.ElementRef }, { type: i2$1.Router }, { type: SiHeaderMenuDirective, decorators: [{
                    type: Optional
                }] }, { type: SiHeaderDropdownMenuDirective, decorators: [{
                    type: Optional
                }] }, { type: SiHeaderItemDirective, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }]; }, propDecorators: { _headerDropdownMenu: [{
                type: ContentChild,
                args: [SiHeaderDropdownMenuDirective]
            }], _dropdownMenu: [{
                type: ContentChild,
                args: [SiDropdownMenuDirective]
            }], links: [{
                type: ContentChildren,
                args: [RouterLink]
            }], linksWithHrefs: [{
                type: ContentChildren,
                args: [RouterLink]
            }], _globalClick: [{
                type: HostListener,
                args: ['document:click', ['$event']]
            }], onEnter: [{
                type: HostListener,
                args: ['mouseenter']
            }] } });

class SiHeaderLinkCloseDirective {
    constructor(navbarDirective) {
        this.navbarDirective = navbarDirective;
        this.closeAll = false;
    }
    _click() {
        if (this.closeAll) {
            this.navbarDirective.closeAll();
        }
        else {
            this.navbarDirective.closeDropdown();
        }
    }
}
SiHeaderLinkCloseDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderLinkCloseDirective, deps: [{ token: SiHeaderDirective }], target: i0.ɵɵFactoryTarget.Directive });
SiHeaderLinkCloseDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiHeaderLinkCloseDirective, selector: "[siHeaderLinkClose]", inputs: { closeAll: "closeAll" }, host: { listeners: { "click": "_click()" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderLinkCloseDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siHeaderLinkClose]',
                }]
        }], ctorParameters: function () { return [{ type: SiHeaderDirective }]; }, propDecorators: { closeAll: [{
                type: Input
            }], _click: [{
                type: HostListener,
                args: ['click']
            }] } });

class SiHeaderLinkDirective {
    constructor(navbarDirective, elementRef, navDropdownMenuDirective, navItemDirective) {
        this.navbarDirective = navbarDirective;
        this.elementRef = elementRef;
        this.navDropdownMenuDirective = navDropdownMenuDirective;
        this.navItemDirective = navItemDirective;
    }
    get _navDropdownToggle() {
        return this.navItemDirective?.hasDropdown;
    }
    get _active() {
        return this.navItemDirective?._headerDropdownMenu?.open;
    }
    _click() {
        if (this.navItemDirective?.hasDropdown) {
            if (!this.navItemDirective._headerDropdownMenu?.open) {
                this.navItemDirective.openDropdown(this);
            }
        }
        else {
            this.navbarDirective.closeAll();
        }
    }
    _focus() {
        this.elementRef.nativeElement.focus();
    }
}
SiHeaderLinkDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderLinkDirective, deps: [{ token: SiHeaderDirective }, { token: i0.ElementRef }, { token: SiHeaderDropdownMenuDirective, optional: true }, { token: SiHeaderItemDirective, optional: true }], target: i0.ɵɵFactoryTarget.Directive });
SiHeaderLinkDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiHeaderLinkDirective, selector: "[siHeaderLink]", host: { listeners: { "click": "_click()" }, properties: { "attr.aria-haspopup": "this._navDropdownToggle", "class.si-header-dropdown-toggle": "this._navDropdownToggle", "attr.aria-expanded": "this._active", "class.active": "this._active" } }, exportAs: ["siHeaderLink"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderLinkDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siHeaderLink]',
                    exportAs: 'siHeaderLink',
                }]
        }], ctorParameters: function () { return [{ type: SiHeaderDirective }, { type: i0.ElementRef }, { type: SiHeaderDropdownMenuDirective, decorators: [{
                    type: Optional
                }] }, { type: SiHeaderItemDirective, decorators: [{
                    type: Optional
                }] }]; }, propDecorators: { _navDropdownToggle: [{
                type: HostBinding,
                args: ['attr.aria-haspopup']
            }, {
                type: HostBinding,
                args: ['class.si-header-dropdown-toggle']
            }], _active: [{
                type: HostBinding,
                args: ['attr.aria-expanded']
            }, {
                type: HostBinding,
                args: ['class.active']
            }], _click: [{
                type: HostListener,
                args: ['click']
            }] } });

class SiIntranetComponent {
}
SiIntranetComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiIntranetComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiIntranetComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiIntranetComponent, selector: "si-intranet", ngImport: i0, template: "<svg id=\"Intranet_Logo\" aria-labelledby=\"intranet-logo-title\" viewBox=\"0 0 45 16\" width=\"45px\" role=\"img\">\n    <title id=\"intranet-logo-title\">Intranet logo</title>\n    <path id=\"Outline\" class=\"st0\" d=\"M45,16H0V0h45V16z M1,15h43V1H1V15z\"/>\n    <path id=\"INTRA\" class=\"st0\" d=\"M7,12.3V4H6v8.3H7z M10.8,12.3V6.9c0-0.6,0-1.2,0-1.6c0.3,0.6,0.6,1.1,0.9,1.6l0,0l3.2,5.3H16V4\n    h-0.9v5.7c0,0.5,0,0.9,0,1.2l0,0l-0.2-0.3c-0.3-0.5-0.5-1-0.8-1.3l0,0L11,4H9.9v8.3H10.8z M21.4,12.3V4.8h2.5V4H18v0.8h2.4v7.5H21.4\n    z M26.8,12.3V8.6h0.3c0.4,0,0.6,0,0.8,0.1s0.4,0.1,0.5,0.2c0.1,0.1,0.2,0.3,0.4,0.4s0.2,0.5,0.4,0.9l0,0l0.9,2.1h1l-1-2.5\n    c-0.2-0.6-0.4-0.9-0.6-1.1s-0.4-0.3-0.7-0.4c0.6-0.1,1-0.4,1.3-0.7s0.5-0.8,0.5-1.4c0-0.8-0.3-1.4-1-1.8c-0.4-0.2-1.1-0.4-2-0.4l0,0\n    h-1.8v8.3H26.8z M27.2,7.8h-0.4v-3h0.6c0.7,0,1.2,0.1,1.5,0.2c0.4,0.2,0.7,0.6,0.7,1.2c0,0.6-0.3,1.1-0.8,1.3\n    C28.4,7.7,27.9,7.8,27.2,7.8L27.2,7.8z M33.1,12.3l1-2.9h3.1l1,2.9h1l-3-8.3h-1.2l-3,8.3H33.1z M36.9,8.6h-2.5l0.8-2.2\n    c0.2-0.6,0.4-1.1,0.5-1.6c0.2,0.5,0.3,1,0.5,1.6l0,0L36.9,8.6z\"/>\n</svg>\n", styles: [":host{display:contents}svg{fill:var(--bs-body-color)}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiIntranetComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-intranet', template: "<svg id=\"Intranet_Logo\" aria-labelledby=\"intranet-logo-title\" viewBox=\"0 0 45 16\" width=\"45px\" role=\"img\">\n    <title id=\"intranet-logo-title\">Intranet logo</title>\n    <path id=\"Outline\" class=\"st0\" d=\"M45,16H0V0h45V16z M1,15h43V1H1V15z\"/>\n    <path id=\"INTRA\" class=\"st0\" d=\"M7,12.3V4H6v8.3H7z M10.8,12.3V6.9c0-0.6,0-1.2,0-1.6c0.3,0.6,0.6,1.1,0.9,1.6l0,0l3.2,5.3H16V4\n    h-0.9v5.7c0,0.5,0,0.9,0,1.2l0,0l-0.2-0.3c-0.3-0.5-0.5-1-0.8-1.3l0,0L11,4H9.9v8.3H10.8z M21.4,12.3V4.8h2.5V4H18v0.8h2.4v7.5H21.4\n    z M26.8,12.3V8.6h0.3c0.4,0,0.6,0,0.8,0.1s0.4,0.1,0.5,0.2c0.1,0.1,0.2,0.3,0.4,0.4s0.2,0.5,0.4,0.9l0,0l0.9,2.1h1l-1-2.5\n    c-0.2-0.6-0.4-0.9-0.6-1.1s-0.4-0.3-0.7-0.4c0.6-0.1,1-0.4,1.3-0.7s0.5-0.8,0.5-1.4c0-0.8-0.3-1.4-1-1.8c-0.4-0.2-1.1-0.4-2-0.4l0,0\n    h-1.8v8.3H26.8z M27.2,7.8h-0.4v-3h0.6c0.7,0,1.2,0.1,1.5,0.2c0.4,0.2,0.7,0.6,0.7,1.2c0,0.6-0.3,1.1-0.8,1.3\n    C28.4,7.7,27.9,7.8,27.2,7.8L27.2,7.8z M33.1,12.3l1-2.9h3.1l1,2.9h1l-3-8.3h-1.2l-3,8.3H33.1z M36.9,8.6h-2.5l0.8-2.2\n    c0.2-0.6,0.4-1.1,0.5-1.6c0.2,0.5,0.3,1,0.5,1.6l0,0L36.9,8.6z\"/>\n</svg>\n", styles: [":host{display:contents}svg{fill:var(--bs-body-color)}\n"] }]
        }] });

class SiLogoComponent {
}
SiLogoComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLogoComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiLogoComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiLogoComponent, selector: "si-logo", ngImport: i0, template: "<svg aria-labelledby=\"siemens-logo\" viewBox=\"0 0 1000 159\" width=\"120px\" role=\"img\">\n    <title id=\"siemens-logo\">Siemens logo</title>\n    <g>\n        <path style=\"fill-rule: evenodd;clip-rule: evenodd;\" d=\"M3.086,152.537V122.46\n                  c17.119,5.388,32.267,8.082,45.444,8.082c18.193,0,27.291-4.809,27.291-14.42c0-3.583-1.324-6.594-3.978-9.032\n                  c-2.714-2.586-9.665-6.171-20.835-10.764c-20.042-8.241-33.111-15.269-39.19-21.082C3.939,67.571,0,57.895,0,46.202\n                  C0,31.144,5.74,19.667,17.212,11.78C28.557,3.962,43.33,0.057,61.554,0.057c10.041,0,24.574,1.848,43.583,5.549v28.933\n                  c-14.144-5.65-27.273-8.469-39.403-8.469c-17.081,0-25.621,4.69-25.621,14.091c0,3.514,1.72,6.38,5.165,8.602\n                  c2.865,1.798,10.759,5.596,23.665,11.406c18.583,8.253,30.954,15.427,37.118,21.529c7.314,7.238,10.978,16.604,10.978,28.084\n                  c0,16.501-7.177,29.088-21.521,37.761c-11.621,7.033-26.69,10.535-45.198,10.535C34.69,158.078,18.942,156.237,3.086,152.537\n                  L3.086,152.537z\"/>\n        <polygon style=\"fill-rule:evenodd;clip-rule:evenodd;\" points=\"141.063,2.704 141.063,2.704 183.603,2.704\n                  183.603,155.001 141.063,155.001\"/>\n        <polygon style=\"fill-rule:evenodd;clip-rule:evenodd;\" points=\"222.616,155.001 222.616,2.704 331.721,2.704\n                  331.721,30.25 263.616,30.25 263.616,64.639 322.898,64.639 322.898,89.765 263.616,89.765 263.616,125.906 333.476,125.906\n                  333.476,155.001 222.616,155.001 \t\t\"/>\n        <polygon style=\"fill-rule:evenodd;clip-rule:evenodd;\" points=\"361.247,155.001 361.247,2.704 416.402,2.704\n                  454.721,100.015 494.001,2.704 546.39,2.704 546.39,155.001 506.056,155.001 506.056,47.171 461.392,156.547 435.023,156.547\n                  391.219,47.171 391.219,155.001 361.247,155.001\"/>\n        <polygon style=\"fill-rule:evenodd;clip-rule:evenodd;\" points=\"585.411,155.001 585.411,2.704 694.514,2.704\n                  694.514,30.25 626.415,30.25 626.415,64.639 685.695,64.639 685.695,89.765 626.415,89.765 626.415,125.906 696.28,125.906\n                  696.28,155.001 585.411,155.001\"/>\n        <polygon style=\"fill-rule:evenodd;clip-rule:evenodd;\" points=\"724.271,155.001 724.271,2.704 773.575,2.704\n                  825.883,104.655 825.883,2.704 855.847,2.704 855.847,155.001 807.943,155.001 754.247,51.678 754.247,155.001 724.271,155.001\n                  \"/>\n        <path style=\"fill-rule:evenodd;clip-rule:evenodd;\" d=\"M886.047,152.537V122.46\n                  c16.974,5.388,32.12,8.082,45.452,8.082c18.195,0,27.282-4.809,27.282-14.42c0-3.583-1.289-6.594-3.854-9.032\n                  c-2.728-2.586-9.708-6.171-20.945-10.764c-19.982-8.173-33.064-15.198-39.199-21.082c-7.875-7.605-11.807-17.317-11.807-29.146\n                  c0-14.993,5.726-26.432,17.21-34.319c11.328-7.818,26.118-11.723,44.344-11.723c10.247,0,23.525,1.627,39.81,4.896l3.761,0.653\n                  v28.933c-14.146-5.65-27.313-8.469-39.508-8.469c-17.016,0-25.503,4.69-25.503,14.091c0,3.514,1.711,6.38,5.147,8.602\n                  c2.73,1.729,10.656,5.529,23.778,11.406c18.442,8.253,30.787,15.427,37.005,21.529c7.325,7.238,10.98,16.604,10.98,28.084\n                  c0,16.501-7.135,29.088-21.406,37.761c-11.689,7.033-26.796,10.535-45.301,10.535\n                  C917.646,158.078,901.891,156.237,886.047,152.537L886.047,152.537z\"/>\n    </g>\n</svg>\n", styles: [":host{display:contents}svg *{fill:var(--si-brand-color)}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLogoComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-logo', template: "<svg aria-labelledby=\"siemens-logo\" viewBox=\"0 0 1000 159\" width=\"120px\" role=\"img\">\n    <title id=\"siemens-logo\">Siemens logo</title>\n    <g>\n        <path style=\"fill-rule: evenodd;clip-rule: evenodd;\" d=\"M3.086,152.537V122.46\n                  c17.119,5.388,32.267,8.082,45.444,8.082c18.193,0,27.291-4.809,27.291-14.42c0-3.583-1.324-6.594-3.978-9.032\n                  c-2.714-2.586-9.665-6.171-20.835-10.764c-20.042-8.241-33.111-15.269-39.19-21.082C3.939,67.571,0,57.895,0,46.202\n                  C0,31.144,5.74,19.667,17.212,11.78C28.557,3.962,43.33,0.057,61.554,0.057c10.041,0,24.574,1.848,43.583,5.549v28.933\n                  c-14.144-5.65-27.273-8.469-39.403-8.469c-17.081,0-25.621,4.69-25.621,14.091c0,3.514,1.72,6.38,5.165,8.602\n                  c2.865,1.798,10.759,5.596,23.665,11.406c18.583,8.253,30.954,15.427,37.118,21.529c7.314,7.238,10.978,16.604,10.978,28.084\n                  c0,16.501-7.177,29.088-21.521,37.761c-11.621,7.033-26.69,10.535-45.198,10.535C34.69,158.078,18.942,156.237,3.086,152.537\n                  L3.086,152.537z\"/>\n        <polygon style=\"fill-rule:evenodd;clip-rule:evenodd;\" points=\"141.063,2.704 141.063,2.704 183.603,2.704\n                  183.603,155.001 141.063,155.001\"/>\n        <polygon style=\"fill-rule:evenodd;clip-rule:evenodd;\" points=\"222.616,155.001 222.616,2.704 331.721,2.704\n                  331.721,30.25 263.616,30.25 263.616,64.639 322.898,64.639 322.898,89.765 263.616,89.765 263.616,125.906 333.476,125.906\n                  333.476,155.001 222.616,155.001 \t\t\"/>\n        <polygon style=\"fill-rule:evenodd;clip-rule:evenodd;\" points=\"361.247,155.001 361.247,2.704 416.402,2.704\n                  454.721,100.015 494.001,2.704 546.39,2.704 546.39,155.001 506.056,155.001 506.056,47.171 461.392,156.547 435.023,156.547\n                  391.219,47.171 391.219,155.001 361.247,155.001\"/>\n        <polygon style=\"fill-rule:evenodd;clip-rule:evenodd;\" points=\"585.411,155.001 585.411,2.704 694.514,2.704\n                  694.514,30.25 626.415,30.25 626.415,64.639 685.695,64.639 685.695,89.765 626.415,89.765 626.415,125.906 696.28,125.906\n                  696.28,155.001 585.411,155.001\"/>\n        <polygon style=\"fill-rule:evenodd;clip-rule:evenodd;\" points=\"724.271,155.001 724.271,2.704 773.575,2.704\n                  825.883,104.655 825.883,2.704 855.847,2.704 855.847,155.001 807.943,155.001 754.247,51.678 754.247,155.001 724.271,155.001\n                  \"/>\n        <path style=\"fill-rule:evenodd;clip-rule:evenodd;\" d=\"M886.047,152.537V122.46\n                  c16.974,5.388,32.12,8.082,45.452,8.082c18.195,0,27.282-4.809,27.282-14.42c0-3.583-1.289-6.594-3.854-9.032\n                  c-2.728-2.586-9.708-6.171-20.945-10.764c-19.982-8.173-33.064-15.198-39.199-21.082c-7.875-7.605-11.807-17.317-11.807-29.146\n                  c0-14.993,5.726-26.432,17.21-34.319c11.328-7.818,26.118-11.723,44.344-11.723c10.247,0,23.525,1.627,39.81,4.896l3.761,0.653\n                  v28.933c-14.146-5.65-27.313-8.469-39.508-8.469c-17.016,0-25.503,4.69-25.503,14.091c0,3.514,1.711,6.38,5.147,8.602\n                  c2.73,1.729,10.656,5.529,23.778,11.406c18.442,8.253,30.787,15.427,37.005,21.529c7.325,7.238,10.98,16.604,10.98,28.084\n                  c0,16.501-7.135,29.088-21.406,37.761c-11.689,7.033-26.796,10.535-45.301,10.535\n                  C917.646,158.078,901.891,156.237,886.047,152.537L886.047,152.537z\"/>\n    </g>\n</svg>\n", styles: [":host{display:contents}svg *{fill:var(--si-brand-color)}\n"] }]
        }] });

class SiHeaderModule {
}
SiHeaderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiHeaderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderModule, declarations: [SiHeaderCollapseDirective,
        SiHeaderContainerDirective,
        SiHeaderDirective,
        SiHeaderDropdownMenuDirective,
        SiHeaderFocusEntryDirective,
        SiHeaderItemDirective,
        SiHeaderLinkCloseDirective,
        SiHeaderLinkDirective,
        SiHeaderMenuDirective,
        SiHeaderTogglerDirective,
        SiIntranetComponent,
        SiLogoComponent], imports: [CommonModule, RouterModule, SiLocalizationModule], exports: [SiHeaderCollapseDirective,
        SiHeaderContainerDirective,
        SiHeaderDirective,
        SiHeaderDropdownMenuDirective,
        SiHeaderFocusEntryDirective,
        SiHeaderItemDirective,
        SiHeaderLinkCloseDirective,
        SiHeaderLinkDirective,
        SiHeaderMenuDirective,
        SiHeaderTogglerDirective,
        SiIntranetComponent,
        SiLogoComponent] });
SiHeaderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderModule, imports: [CommonModule, RouterModule, SiLocalizationModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiHeaderModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        SiHeaderCollapseDirective,
                        SiHeaderContainerDirective,
                        SiHeaderDirective,
                        SiHeaderDropdownMenuDirective,
                        SiHeaderFocusEntryDirective,
                        SiHeaderItemDirective,
                        SiHeaderLinkCloseDirective,
                        SiHeaderLinkDirective,
                        SiHeaderMenuDirective,
                        SiHeaderTogglerDirective,
                        SiIntranetComponent,
                        SiLogoComponent,
                    ],
                    imports: [CommonModule, RouterModule, SiLocalizationModule],
                    exports: [
                        SiHeaderCollapseDirective,
                        SiHeaderContainerDirective,
                        SiHeaderDirective,
                        SiHeaderDropdownMenuDirective,
                        SiHeaderFocusEntryDirective,
                        SiHeaderItemDirective,
                        SiHeaderLinkCloseDirective,
                        SiHeaderLinkDirective,
                        SiHeaderMenuDirective,
                        SiHeaderTogglerDirective,
                        SiIntranetComponent,
                        SiLogoComponent,
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SiHeaderCollapseDirective, SiHeaderContainerDirective, SiHeaderDirective, SiHeaderDropdownMenuDirective, SiHeaderFocusEntryDirective, SiHeaderItemDirective, SiHeaderLinkCloseDirective, SiHeaderLinkDirective, SiHeaderMenuDirective, SiHeaderModule, SiHeaderTogglerDirective, SiIntranetComponent, SiLogoComponent };
